// src/components/Admin/SeriesFormModal.tsx
import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import type { Series } from '../../types/series';
import seriesService from '../../services/seriesService';
import { useToast } from '../../hooks/useToast';

interface SeriesFormModalProps {
  isOpen: boolean;
  mode: 'add' | 'edit' | 'view';
  series?: Series;
  onClose: () => void;
  onSave: (series: Series) => void;
}

const SeriesFormModal: React.FC<SeriesFormModalProps> = ({
  isOpen,
  mode,
  series,
  onClose,
  onSave,
}) => {
  const { showError, showSuccess } = useToast();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    seriesType: 'CONTENT_SERIES' as 'CINEMA_SERIES' | 'CONTENT_SERIES' | 'PODCAST_SERIES' | 'ANIMATION_SERIES',
    category: '',
    tags: '',
    status: 'DRAFT' as 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'COMPLETED',
    isFeatured: false,
    isPremium: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when modal opens or series changes
  useEffect(() => {
    if (series && (mode === 'edit' || mode === 'view')) {
      setFormData({
        title: series.title || '',
        description: series.description || '',
        seriesType: series.seriesType,
        category: series.category || '',
        tags: (series as Series & { tags?: string }).tags || '',
        status: series.status,
        isFeatured: series.isFeatured || false,
        isPremium: series.isPremium || false,
      });
    } else if (mode === 'add') {
      setFormData({
        title: '',
        description: '',
        seriesType: 'CONTENT_SERIES',
        category: '',
        tags: '',
        status: 'DRAFT',
        isFeatured: false,
        isPremium: false,
      });
    }
    setErrors({});
  }, [series, mode, isOpen]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.seriesType) {
      newErrors.seriesType = 'Series type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'view') return;

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      let savedSeries: Series;
      
      if (mode === 'add') {
        savedSeries = await seriesService.createSeries(formData);
        showSuccess(`Series "${formData.title}" created successfully`);
      } else if (mode === 'edit' && series) {
        savedSeries = await seriesService.updateSeries(series.id, formData);
        showSuccess(`Series "${formData.title}" updated successfully`);
      } else {
        throw new Error('Invalid mode or missing series data');
      }

      onSave(savedSeries);
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save series';
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const seriesTypeOptions = [
    { value: 'CINEMA_SERIES', label: 'Cinema Series', description: 'Movie series and cinematic content' },
    { value: 'CONTENT_SERIES', label: 'Content Series', description: 'General content and article series' },
    { value: 'PODCAST_SERIES', label: 'Podcast Series', description: 'Audio podcast series' },
    { value: 'ANIMATION_SERIES', label: 'Animation Series', description: 'Animated video series' },
  ];

  const statusOptions = [
    { value: 'DRAFT', label: 'Draft', description: 'Not published yet' },
    { value: 'PUBLISHED', label: 'Published', description: 'Live and visible to users' },
    { value: 'ARCHIVED', label: 'Archived', description: 'Hidden from public view' },
    { value: 'COMPLETED', label: 'Completed', description: 'Series is finished' },
  ];

  const getModalTitle = () => {
    switch (mode) {
      case 'add':
        return 'Create New Series';
      case 'edit':
        return `Edit: ${series?.title || 'Series'}`;
      case 'view':
        return `View: ${series?.title || 'Series'}`;
      default:
        return 'Series Form';
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {getModalTitle()}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              disabled={mode === 'view'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              placeholder="Enter series title"
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600">{errors.title}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              disabled={mode === 'view'}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              placeholder="Enter series description"
            />
          </div>

          {/* Series Type and Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Series Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Series Type *
              </label>
              <select
                value={formData.seriesType}
                onChange={(e) => handleInputChange('seriesType', e.target.value)}
                disabled={mode === 'view'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              >
                {seriesTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {seriesTypeOptions.find(opt => opt.value === formData.seriesType)?.description}
              </p>
              {errors.seriesType && (
                <p className="mt-1 text-sm text-red-600">{errors.seriesType}</p>
              )}
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status *
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                disabled={mode === 'view'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {statusOptions.find(opt => opt.value === formData.status)?.description}
              </p>
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              disabled={mode === 'view'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              placeholder="Enter category (optional)"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => handleInputChange('tags', e.target.value)}
              disabled={mode === 'view'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              placeholder="Enter tags separated by commas (e.g. action, thriller, drama)"
            />
            <p className="mt-1 text-sm text-gray-500">
              Separate multiple tags with commas. Leave empty for default tags based on category and series type.
            </p>
          </div>

          {/* Options */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Options</h3>
            <div className="flex items-center space-x-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isFeatured}
                  onChange={(e) => handleInputChange('isFeatured', e.target.checked)}
                  disabled={mode === 'view'}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="ml-2 text-sm text-gray-700">Featured Series</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isPremium}
                  onChange={(e) => handleInputChange('isPremium', e.target.checked)}
                  disabled={mode === 'view'}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="ml-2 text-sm text-gray-700">Premium Series</span>
              </label>
            </div>
          </div>

          {/* Show existing series info for view/edit mode */}
          {(mode === 'edit' || mode === 'view') && series && (
            <div className="bg-gray-50 rounded-lg p-4 border-t">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Series Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Author: </span>
                  <span className="text-gray-600">{series.author?.name || 'Unknown'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Seasons: </span>
                  <span className="text-gray-600">{series.totalSeasons || 0}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Episodes: </span>
                  <span className="text-gray-600">{series.totalEpisodes || 0}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Created: </span>
                  <span className="text-gray-600">
                    {new Date(series.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
            >
              {mode === 'view' ? 'Close' : 'Cancel'}
            </button>
            {mode !== 'view' && (
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isLoading || !formData.title.trim()}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 focus:ring-2 focus:ring-orange-500 disabled:opacity-50 flex items-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {mode === 'add' ? 'Create Series' : 'Save Changes'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeriesFormModal;